import {
  Button,
  Box,
  Text,
  Combobox,
  Spinner,
  Stack,
  ButtonGroup,
  Message
} from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { useQuery } from "@tanstack/react-query";
import { EmailServiceApi } from "api/services";
import { Table } from "components/Datatable";
import { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Modal } from "components/Modal";

const {
  emailType: { authV2EmailTypeAvailableList: getAllAvailableEmailTypes }
} = EmailServiceApi;

export const ProgramNotificationsList = () => {
  const [editMode, setEditMode] = useState(false);

  const programNotificationTypes: ProgramService.ProgramNotificationType[] = [
    "AutomatedOffer",
    "ManualOffer",
    "AddSellers"
  ];

  const { setValue, control, watch } =
    useFormContext<ProgramService.ProgramModel>();
  const { fields, append, remove } = useFieldArray({
    name: "notifications",
    control
  });
  const notifications = watch("notifications");

  const { data: emailTypes, isLoading } = useQuery(["getAllEmailTypes"], () =>
    getAllAvailableEmailTypes()
  );
  const emailTypesOnProgramInstance = emailTypes?.filter(
    emailType => emailType.type === "programInstance"
  );

  if (isLoading) return <Spinner centered />;

  return (
    <Stack spacing={"gutter"}>
      <Table
        data={notifications}
        pagination={false}
        columns={[
          {
            name: "Notification type",
            cell: row => <Text>{row.type}</Text>
          },
          {
            name: "Email type",
            cell: row => <Text>{row.emailType}</Text>
          },
          {
            name: "",
            button: true,
            width: "42px",
            cell: (_, index) => (
              <Button
                logLabel="remove-notification-button"
                variant="text"
                onClick={() => {
                  remove(index);
                }}
              >
                <i className="uil uil-trash-alt is-size-4" />
              </Button>
            )
          }
        ]}
      />
      <Button
        style={{ display: editMode ? "none" : "block" }}
        logLabel="add-program-notification-button"
        variant="text"
        onClick={() => {
          setEditMode(true);
        }}
      >
        + Add or edit notification
      </Button>
      {editMode && (
        <Modal show={editMode} onClose={() => setEditMode(false)}>
          <Modal.Card style={{ overflow: "visible" }}>
            <Modal.Card.Head onClose={() => setEditMode(false)}>
              <Modal.Card.Title>Edit notifications</Modal.Card.Title>
            </Modal.Card.Head>
            <Stack spacing={"s"}>
              <Modal.Card.Body style={{ overflow: "visible" }}>
                <Stack spacing={"s"}>
                  {notifications.length === 0 && (
                    <Message type="warning">No notifications yet</Message>
                  )}
                  {editMode &&
                    fields &&
                    fields.map((field, index) => {
                      return (
                        <Box key={field.id}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="end"
                          >
                            <Controller
                              control={control}
                              name={`notifications.${index}.type`}
                              render={({ field }) => (
                                <div
                                  style={{ width: "100%", marginRight: "12px" }}
                                >
                                  <Combobox
                                    placeholder="Select notification type"
                                    items={programNotificationTypes}
                                    defaultItem={field.value}
                                    onChange={type => field.onChange(type)}
                                    label="Notification type"
                                    isSearchable
                                  />
                                </div>
                              )}
                            />
                            <Controller
                              control={control}
                              name={`notifications.${index}.emailType`}
                              render={({ field }) => (
                                <div style={{ width: "100%" }}>
                                  <Combobox
                                    defaultItem={{
                                      name: field.value,
                                      id: field.value,
                                      type: "programInstance"
                                    }}
                                    items={emailTypesOnProgramInstance ?? []}
                                    valueObjectIndexAccessor="name"
                                    onChange={emailType => {
                                      field.onChange(emailType.name);
                                    }}
                                    label="Email type"
                                    placeholder="Select email type"
                                    isSearchable
                                  />
                                </div>
                              )}
                            />
                            <Button
                              style={{ marginLeft: "12px" }}
                              logLabel="delete-cleanup-url-button"
                              variant="text"
                              onClick={() => remove(index)}
                            >
                              <i className="uil uil-trash-alt is-size-4" />
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                </Stack>
              </Modal.Card.Body>
              <Modal.Card.Foot style={{ flexDirection: "row" }}>
                <ButtonGroup display="flex">
                  <Button
                    logLabel="add-program-notification-button"
                    variant="text"
                    onClick={() =>
                      append({
                        type: programNotificationTypes[0],
                        emailType: emailTypesOnProgramInstance?.[0]?.name ?? ""
                      })
                    }
                  >
                    + Add another notification
                  </Button>
                  <Button
                    logLabel="save-notifications-button"
                    onClick={() => {
                      setValue("notifications", [...notifications]);
                      setEditMode(false);
                    }}
                  >
                    Update notifications
                  </Button>
                </ButtonGroup>
              </Modal.Card.Foot>
            </Stack>
          </Modal.Card>
        </Modal>
      )}
    </Stack>
  );
};
